import React from 'react'
import { observer } from 'mobx-react-lite'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'
import { endOfMonth } from 'date-fns/endOfMonth'

import Box from '@mui/material/Box'

import type { DateRange } from '@mui/x-date-pickers-pro'

import {
  Loader,
  useAccountStore,
  useGlobalUiStore,
  WidgetContainer,
  useEvent,
  ANALYTICS_EVENTS,
  useGlobalCopyStore,
  useAppConfig,
  useCategoryStore,
  useTransactionStore,
  useWidgetLoadTimer,
} from '../common'

import type { WidgetProps } from '../core'

import Spending from './components/Spending'
import { parseISO } from 'date-fns/parseISO'

// Identifies the totals request implied by a range + account selection. Guids
// are sorted so a reordered-but-equivalent selection compares equal.
const buildLoadKey = (start: Date, end: Date, guids: string[]) =>
  `${start.getTime()}-${end.getTime()}-${[...guids].sort().join(',')}`

interface RSpendingWidgetProps extends WidgetProps {
  onBackClick?: () => void
}

const SpendingWidget: React.FC<RSpendingWidgetProps> = ({ onBackClick, onMenuClick, sx = {} }) => {
  const { isAccountDataLoaded, loadAccountData } = useAccountStore()
  const { categoriesLoaded, dateRangeTotalsLoaded, loadCategories, loadDateRangeCategoryTotals } =
    useCategoryStore()
  const { isTransactionDataLoaded, loadTransactionData } = useTransactionStore()
  const { spending: spendingCopy } = useGlobalCopyStore()
  const {
    displayedDateRange,
    isInitialized,
    setDisplayedDate,
    selectedAccounts,
    selectedAccountGuids,
  } = useGlobalUiStore()
  const { onEvent } = useEvent()

  const { clientConfig } = useAppConfig()

  useWidgetLoadTimer({
    widgetName: 'SpendingWidget',
    isLoaded: dateRangeTotalsLoaded,
  })

  // Tracks the request the mount effect already issued, so the change effect
  // doesn't repeat it on first paint. Only ever compared against a key built
  // from this effect's own deps, so a stale key cannot suppress a retry the
  // user can actually trigger: any retry-worthy action changes the range or the
  // account selection, which changes the key. (MEW-3910)
  const lastLoadedKeyRef = React.useRef<string | null>(null)
  // The change effect's first run happens in the same commit as the mount
  // effect, when `displayedDateRange` may still hold a previously visited
  // widget's range. The mount effect owns that initial load, so the change
  // effect skips its first run rather than fetching a range nobody asked for.
  const hasRunChangeEffectRef = React.useRef(false)

  React.useEffect(() => {
    const dlp = clientConfig?.master?.deep_link_params

    const date_range = dlp?.date_range
      ? ([parseISO(dlp.date_range.from), parseISO(dlp.date_range.to)] as DateRange<Date>)
      : ([startOfMonth(startOfToday()), endOfMonth(startOfToday())] as DateRange<Date>)

    const startDate = date_range[0] as Date
    const endDate = date_range[1] as Date

    setDisplayedDate(startDate, endDate)

    if (!isAccountDataLoaded) {
      loadAccountData().finally()
    }
    if (!categoriesLoaded) {
      loadCategories().finally()
    }
    if (!isTransactionDataLoaded) {
      loadTransactionData().finally()
    }

    // Load with the dates we just computed rather than reading from the store,
    // which may still hold a stale range set by a previously visited widget.
    if (isInitialized && isAccountDataLoaded) {
      lastLoadedKeyRef.current = buildLoadKey(startDate, endDate, selectedAccountGuids)
      loadDateRangeCategoryTotals(selectedAccounts, startDate, endDate).finally()
    }

    onEvent(ANALYTICS_EVENTS.SPENDING_VIEW)
  }, [])

  // Reload whenever the date range or the account selection genuinely changes.
  // Depends on primitives, not on `displayedDateRange` / `selectedAccounts`,
  // whose object identities change even when the values do not. (MEW-3910)
  React.useEffect(() => {
    // Skipped before the readiness check so a cold start (where the mount effect
    // could not load yet) still reaches this effect once the store is ready.
    if (!hasRunChangeEffectRef.current) {
      hasRunChangeEffectRef.current = true
      return
    }

    if (!isInitialized || !isAccountDataLoaded) return

    const loadKey = buildLoadKey(
      displayedDateRange.start,
      displayedDateRange.end,
      selectedAccountGuids,
    )
    if (lastLoadedKeyRef.current === loadKey) return

    lastLoadedKeyRef.current = loadKey
    loadDateRangeCategoryTotals(
      selectedAccounts,
      displayedDateRange.start,
      displayedDateRange.end,
    ).finally()
  }, [
    displayedDateRange.start.getTime(),
    displayedDateRange.end.getTime(),
    isInitialized,
    isAccountDataLoaded,
    selectedAccountGuids.join(','),
  ])

  const handleDateChanged = (range: DateRange<Date>) => {
    setDisplayedDate(range[0] as Date, range[1] as Date)
  }

  if (!isInitialized || !isAccountDataLoaded || !dateRangeTotalsLoaded) {
    return <Loader />
  }

  return (
    <WidgetContainer
      calendarActions={{ onRangeChanged: handleDateChanged }}
      onAccountsFilterClick={() => onEvent(ANALYTICS_EVENTS.SPENDING_CLICK_FILTER)}
      onBackClick={onBackClick}
      onMenuClick={onMenuClick}
      sx={sx}
      title={spendingCopy.title}
    >
      <Box width="100%">
        <Spending />
      </Box>
    </WidgetContainer>
  )
}

export default observer(SpendingWidget)
